/**
 * @file pages/well/OrderPanel.js
 * @description Well Butler — "Order these parts" panel under a sizing result.
 *
 * Turns the recommendation into a Hajoca order sheet: matches the recommended
 * pump/tank/switch against the catalog (data-driven — no hardcoded products;
 * honest "not in catalog, quote at counter" lines where the sample has no row),
 * lets the tech check/uncheck + edit footage, then emails the sheet through the
 * suite's live SES pipe (order/send channel:'email'). Seed of Supply Butler.
 * Added 2026-07-03 (B2).
 */

/*****************************************************************
 *  TABLE OF CONTENTS                                             *
 *  SECTION 1 — Imports and fixed values                          *
 *  SECTION 2 — Small helpers (money, horsepower spellings)       *
 *  SECTION 3 — Catalog matching (one finder per part)            *
 *  SECTION 4 — The panel itself (build the sheet, email it)      *
 *****************************************************************/

/*****************************************************************
 *  SECTION 1 — IMPORTS AND FIXED VALUES                          *
 *****************************************************************/

import React, { useState } from 'react';
import { Button, Card, Input } from '../../design-system';
import { wellApi } from '../../lib/wellApi';
import { useAuth } from '../../contexts/AuthContext';

const SMALL_TEXT = { fontSize: 'var(--text-body-sm)' };   // the panel's line text size

const DEFAULT_SWITCH_BAND = '40-60';          // pressure-switch band when the form has none
const EXTRA_FEET_FOR_WIRE_AND_PIPE = 20;      // slack added over the drop length
const TANK_MATCH_MIN_GALLONS = 12;            // a catalog tank within 12 gal is "close"
const TANK_MATCH_FRACTION = 0.25;             // ...or within 25% of the sized volume

/*****************************************************************
 *  SECTION 2 — SMALL HELPERS                                     *
 *****************************************************************/

/* ---- hpSpellings(horsepower) -----------------------------------
   The catalog writes horsepower as text. 1.5 → ['1 1/2HP', '11/2HP'].
   In: a number.  Out: an array of spellings to look for.         */
function hpSpellings(horsepower) {
  const spellingsByHorsepower = {
    0.5: ['1/2HP'],
    0.75: ['3/4HP'],
    1: ['1HP'],
    1.5: ['1 1/2HP', '11/2HP'],
    2: ['2HP'],
    3: ['3HP'],
    5: ['5HP'],
  };
  const knownSpellings = spellingsByHorsepower[Number(horsepower)];
  if (knownSpellings) {
    return knownSpellings;
  }
  return [String(horsepower) + 'HP'];
}

/* ---- money(cents) ----------------------------------------------
   Shows a price in dollars, or "quote at counter" when unpriced.  */
function money(cents) {
  if (cents == null) {
    return 'quote at counter';
  }
  return '$' + (cents / 100).toFixed(2);
}

/* ---- itemText(catalogItem) -------------------------------------
   Title + description in capitals, for simple "does it say" tests. */
function itemText(catalogItem) {
  const title = catalogItem.title || '';
  const description = catalogItem.description || '';
  return (title + ' ' + description).toUpperCase();
}

/* ---- lineFromCatalogItem(catalogItem) --------------------------
   Turns a matched catalog row into one checked line on the sheet. */
function lineFromCatalogItem(catalogItem) {
  return {
    on: true,
    hajocaProductId: catalogItem.hajocaProductId,
    label: catalogItem.title,
    sub: catalogItem.description,
    priceCents: catalogItem.priceCents,
    quantity: 1,
    uom: catalogItem.uom || 'each',
  };
}

/*****************************************************************
 *  SECTION 3 — CATALOG MATCHING                                  *
 *  What it does: asks the server's catalog for each part and     *
 *  picks the matching row, or writes an honest "counter quote"   *
 *  line when the loaded catalog has no match.                    *
 *  Reads: GET /api/v1/well/catalog.   Writes: nothing.           *
 *****************************************************************/

/* ---- readCatalogItems(query) -----------------------------------
   Asks the catalog and returns its items (empty list on a miss).
   WHY (2026-10-07, James): defect #168 — this used to call
   wellApi.catalog(), which does not exist, so building the sheet
   ALWAYS failed with "... is not a function". The real call is
   wellApi.browseCatalog().                                        */
async function readCatalogItems(query) {
  const envelope = await wellApi.browseCatalog(query);
  const wasFound = envelope.success && envelope.data && envelope.data.items;
  if (!wasFound) {
    return [];
  }
  return envelope.data.items;
}

/* ---- findPumpLine(pump) ----------------------------------------
   Matches the sized pump by GPM class + horsepower text.          */
async function findPumpLine(pump) {
  const submersiblePumps = await readCatalogItems({ category: 'pumps_submersible', limit: 30 });
  const wantedHorsepower = hpSpellings(pump.hp);
  const gpmClassText = pump.gpmClass != null ? String(pump.gpmClass) : null;

  const matchingPump = submersiblePumps.find((catalogItem) => {
    const text = itemText(catalogItem);
    const horsepowerMatches = wantedHorsepower.some((spelling) => text.includes(spelling));
    const titleUpper = (catalogItem.title || '').toUpperCase();
    const classMatches = gpmClassText ? titleUpper.includes(gpmClassText) : true;
    return horsepowerMatches && classMatches;
  });

  if (matchingPump) {
    return lineFromCatalogItem(matchingPump);
  }

  const gpmWords = pump.gpmClass != null ? pump.gpmClass : '?';
  const horsepowerWords = pump.hp != null ? pump.hp : '?';
  const sizedName = (pump.brand || '') + ' ' + (pump.modelCode || '');
  return {
    on: true,
    description: 'Submersible pump ' + gpmWords + ' GPM class, ' + horsepowerWords
      + ' HP (sized: ' + sizedName + ') — NOT IN HAJOCA SAMPLE, quote at counter',
    label: 'Pump: ' + sizedName,
    sub: 'not in catalog — counter quote',
    priceCents: null,
    quantity: 1,
    uom: 'each',
  };
}

/* ---- findTankLine(tank) ----------------------------------------
   Matches the sized tank by the gallons written in the catalog.   */
async function findTankLine(tank) {
  const pressureTanks = await readCatalogItems({ category: 'pressure_tanks', limit: 20 });
  const neededGallons = tank.totalVolumeGal;

  let matchingTank = null;
  if (neededGallons) {
    const allowedDifference = Math.max(TANK_MATCH_MIN_GALLONS, neededGallons * TANK_MATCH_FRACTION);
    matchingTank = pressureTanks.find((catalogItem) => {
      const gallonsMatch = itemText(catalogItem).match(/([0-9]+(?:\.[0-9]+)?)\s*GALLON/i);
      if (!gallonsMatch) {
        return false;
      }
      const catalogGallons = Number(gallonsMatch[1]);
      return Math.abs(catalogGallons - neededGallons) <= allowedDifference;
    });
  }

  if (matchingTank) {
    return lineFromCatalogItem(matchingTank);
  }

  const gallonWords = neededGallons != null ? neededGallons : '?';
  const sizedName = (tank.brand || '') + ' ' + (tank.model || '');
  return {
    on: true,
    description: 'Pressure tank ~' + gallonWords + ' gal (sized: ' + sizedName + ') — verify at counter',
    label: 'Tank: ' + sizedName,
    sub: 'closest catalog match not found — counter quote',
    priceCents: null,
    quantity: 1,
    uom: 'each',
  };
}

/* ---- findSwitchLine(form) --------------------------------------
   Matches a pressure switch carrying the cut-in/cut-out band.
   Out: a line, or null when the catalog has none (left off).      */
async function findSwitchLine(form) {
  const hasBothPressures = form && form.cutInPsi && form.cutOutPsi;
  const switchBand = hasBothPressures ? form.cutInPsi + '-' + form.cutOutPsi : DEFAULT_SWITCH_BAND;
  const pressureSwitches = await readCatalogItems({ q: 'well pump switch', limit: 10 });
  const matchingSwitch = pressureSwitches.find((catalogItem) => {
    const plainText = (catalogItem.title || '') + ' ' + (catalogItem.description || '');
    return plainText.includes(switchBand);
  });
  if (!matchingSwitch) {
    return null;
  }
  return lineFromCatalogItem(matchingSwitch);
}

/* ---- findSpliceKitLine() ---------------------------------------
   The first heat-shrink splice kit in the catalog, or null.       */
async function findSpliceKitLine() {
  const spliceKits = await readCatalogItems({ q: 'heat shrink', limit: 5 });
  if (!spliceKits[0]) {
    return null;
  }
  return lineFromCatalogItem(spliceKits[0]);
}

/* ---- buildFootageLines(form) -----------------------------------
   Wire, drop pipe and the down-well kit. The sample catalog does
   not carry these, so they are honest counter-quote lines.        */
function buildFootageLines(form) {
  const dropFeet = Number(form && form.dropPipeLengthFt) || 0;
  if (dropFeet <= 0) {
    return [];
  }
  const feetToOrder = dropFeet + EXTRA_FEET_FOR_WIRE_AND_PIPE;
  return [
    {
      on: true,
      description: 'Submersible drop wire (gauge per wire-run answer; match pump 2-wire/3-wire)'
        + ' — NOT IN HAJOCA SAMPLE, quote at counter',
      label: 'Drop wire',
      sub: 'gauge per wire-run · counter quote',
      priceCents: null,
      quantity: feetToOrder,
      uom: 'ft',
    },
    {
      on: true,
      description: '1-1/4in SIDR poly drop pipe 160psi — NOT IN HAJOCA SAMPLE, quote at counter',
      label: 'Poly drop pipe',
      sub: 'counter quote',
      priceCents: null,
      quantity: feetToOrder,
      uom: 'ft',
    },
    {
      on: true,
      description: 'Torque arrestor + safety rope + well seal — NOT IN HAJOCA SAMPLE, quote at counter',
      label: 'Down-well kit',
      sub: 'counter quote',
      priceCents: null,
      quantity: 1,
      uom: 'kit',
    },
  ];
}

/*****************************************************************
 *  SECTION 4 — THE PANEL                                         *
 *  What it does: "Build a Hajoca order sheet" fills the list;    *
 *  "Email the order sheet" builds the order on the server and    *
 *  emails it to the address in the box.                          *
 *  Writes: a SupplierOrder row (server) when the sheet is sent.  *
 *****************************************************************/

/* ---- OrderPanel({ data, form }) --------------------------------
   In: the sizing result (data) and the form the tech filled in.
   Out: the panel. Never sends anything without a tap.             */
export default function OrderPanel({ data, form }) {
  const auth = useAuth();
  const signedInEmail = (auth && auth.user && auth.user.email) || '';   // the person using the app

  const [lines, setLines] = useState(null);          // null = sheet not built yet
  const [isBusy, setIsBusy] = useState(false);       // true while a call is in flight
  // WHY (2026-10-07, James): defect #168 — this box was pre-filled with one
  // staff member's company address, so every crew's sheet went to him. It now
  // starts with the signed-in person's own address; they can change it.
  const [sendTo, setSendTo] = useState(signedInEmail);
  const [receipt, setReceipt] = useState(null);      // the server's send receipt
  const [errorMessage, setErrorMessage] = useState(null);

  const pump = data.recommendedPump || {};
  const tank = data.recommendedTank || {};

  /* ---- buildSheet() --------------------------------------------
     Looks up each part and fills the sheet. Never sends anything. */
  async function buildSheet() {
    setIsBusy(true);
    setErrorMessage(null);
    try {
      const sheetLines = [];
      sheetLines.push(await findPumpLine(pump));
      sheetLines.push(await findTankLine(tank));

      const switchLine = await findSwitchLine(form);
      if (switchLine) {
        sheetLines.push(switchLine);
      }

      const spliceKitLine = await findSpliceKitLine();
      if (spliceKitLine) {
        sheetLines.push(spliceKitLine);
      }

      buildFootageLines(form).forEach((footageLine) => sheetLines.push(footageLine));
      setLines(sheetLines);
    } catch (problem) {
      setErrorMessage('Could not build the sheet: ' + problem.message);
    }
    setIsBusy(false);
  }

  /* ---- emailSheet() --------------------------------------------
     Builds the priced order on the server, then emails it.
     The supply-house name comes from the server's one default.    */
  async function emailSheet() {
    setIsBusy(true);
    setErrorMessage(null);
    setReceipt(null);
    try {
      const checkedLines = lines.filter((line) => line.on);
      const orderLines = checkedLines.map((line) => ({
        hajocaProductId: line.hajocaProductId || undefined,
        description: line.hajocaProductId ? undefined : line.description,
        quantity: Number(line.quantity) || 1,
        uom: line.uom,
      }));

      const builtOrder = await wellApi.buildOrder({ lines: orderLines, channel: 'email' });
      if (!builtOrder.success) {
        const buildProblem = (builtOrder.error && builtOrder.error.message) || 'order build failed';
        throw new Error(buildProblem);
      }

      const sizedPumpName = (pump.brand || '') + ' ' + (pump.modelCode || '');
      const totalHeadWords = data.tdhFt != null ? data.tdhFt : '?';
      const sentOrder = await wellApi.sendOrder({
        orderId: builtOrder.data.orderId || builtOrder.meta.persistedId,
        order: {
          channel: 'email',
          supplierContact: sendTo,
          placedByName: 'Well Butler sizing',
          notes: 'From pump sizing: ' + sizedPumpName + ', ' + totalHeadWords
            + ' ft TDH. Verify 2-wire vs 3-wire before pulling wire.',
          lines: builtOrder.data.lines,
        },
      });
      if (!sentOrder.success) {
        const sendProblem = (sentOrder.error && sentOrder.error.message) || 'send failed';
        throw new Error(sendProblem);
      }
      setReceipt(sentOrder.data);
    } catch (problem) {
      setErrorMessage(problem.message);
    }
    setIsBusy(false);
  }

  /* ---- toggleLine(lineIndex) / setLineQuantity(lineIndex, value) ---- */
  function toggleLine(lineIndex) {
    setLines(lines.map((line, index) => (index === lineIndex ? { ...line, on: !line.on } : line)));
  }

  function setLineQuantity(lineIndex, newQuantity) {
    setLines(lines.map((line, index) => (index === lineIndex ? { ...line, quantity: newQuantity } : line)));
  }

  const canSend = !isBusy && sendTo.trim() !== '';   // honest: no address, no send button

  return (
    <Card variant="outline" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
      <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-2)' }}>Order these parts</div>

      {!lines && (
        <Button fullWidth variant="primary" size="lg" onClick={buildSheet} disabled={isBusy}>
          {isBusy ? 'Building the sheet…' : 'Build a Hajoca order sheet'}
        </Button>
      )}

      {lines && lines.map((line, lineIndex) => (
        <div
          key={lineIndex}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-1) 0', ...SMALL_TEXT }}
        >
          <input
            type="checkbox"
            checked={line.on}
            onChange={() => toggleLine(lineIndex)}
            aria-label={'Include ' + line.label}
            style={{ width: 24, height: 24 }}
          />
          <div style={{ flex: 1 }}>
            <div>{line.label}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-xs, 12px)' }}>
              {(line.sub || '').slice(0, 70)}
            </div>
          </div>
          <input
            type="number"
            value={line.quantity}
            min="1"
            aria-label={'How many ' + line.label}
            onChange={(event) => setLineQuantity(lineIndex, event.target.value)}
            style={{ width: 64, minHeight: 44, textAlign: 'right', borderRadius: 8, border: '1px solid var(--border-default)' }}
          />
          <span style={{ width: 34, color: 'var(--text-secondary)' }}>{line.uom}</span>
          <span style={{ width: 92, textAlign: 'right' }}>{money(line.priceCents)}</span>
        </div>
      ))}

      {lines && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Input
            label="Email the sheet to"
            type="email"
            value={sendTo}
            onChange={(event) => setSendTo(event.target.value)}
          />
          <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-xs, 12px)', marginTop: 4 }}>
            Starts with your own address. Change it to send the sheet somewhere else.
          </div>
          <Button
            fullWidth
            variant="primary"
            size="lg"
            onClick={emailSheet}
            disabled={!canSend}
            style={{ marginTop: 'var(--space-2)' }}
          >
            {isBusy ? 'Sending…' : 'Email the order sheet'}
          </Button>
        </div>
      )}

      {receipt && (
        <div style={{ marginTop: 'var(--space-2)', color: receipt.sent ? 'var(--success, #1a7a3c)' : 'var(--text-primary)', ...SMALL_TEXT }}>
          {receipt.sent
            ? '✅ Sent: sheet emailed to ' + receipt.to + ' (' + receipt.transport + ')'
            : '⚠ Not sent: the sheet was made but no email went out. ' + (receipt.transportNote || '')}
        </div>
      )}

      {errorMessage && (
        <div style={{ marginTop: 'var(--space-2)', color: 'var(--danger, #b3261e)', ...SMALL_TEXT }}>
          ⚠ Problem: {errorMessage}
        </div>
      )}

      <div style={{ marginTop: 'var(--space-2)', color: 'var(--text-secondary)', fontSize: 'var(--text-body-xs, 12px)' }}>
        Prices are catalog + your markup. Lines marked "counter quote" aren't in the loaded catalog sample yet — the full Hajoca export fills those in.
      </div>
    </Card>
  );
}
